// UI LANGUAGE HOOKS — CCT-284.
//
// `useT()` returns the translate function bound to the dictionary currently active. Screens never
// resolve the language themselves: a screen that picked its own would drift from the rest of the
// app the day the resolution rule changes.
//
// ⚠️ THE STATE LIVES IN THE STORE, NOT IN THE HOOK. Before CCT-284 each `useT` held its own copy
// of the language; that was harmless while every dictionary was bundled and switching was
// synchronous. It stops being harmless once a dictionary is DOWNLOADED: two screens would render
// two different languages for as long as the fetch lasted. The hook now only subscribes.
import { useCallback, useEffect, useSyncExternalStore } from 'react';

import { DEFAULT_UI_LANGUAGE, translate, uiLanguageFromUrl, type MessageKey } from './index';
import { boot, getLanguage, getMessages, setLanguage as storeSetLanguage,
         subscribe } from './store';

/** The active UI language, and a setter that downloads, caches, applies and persists it. */
export function useUiLanguage() {
  const language = useSyncExternalStore(subscribe, getLanguage, getLanguage);

  // Boot once: adopt the stored preference (or `?lang=`) and load its dictionary. Idempotent —
  // re-running it simply re-applies the same language.
  useEffect(() => { void boot(uiLanguageFromUrl()); }, []);

  const setLanguage = useCallback((v: string) => { void storeSetLanguage(v); }, []);
  return { language, setLanguage };
}

/**
 * The translate function bound to the active dictionary.
 *
 * ⚠️ IT SUBSCRIBES TO THE MESSAGES, NOT ONLY TO THE LANGUAGE CODE. When a download completes the
 * code has not changed — it was set the moment the user tapped — but the dictionary has. A hook
 * watching only the code would leave every screen on English until something else re-rendered it.
 */
export function useT() {
  const messages = useSyncExternalStore(subscribe, getMessages, getMessages);
  return useCallback(
    (key: MessageKey, values?: Record<string, unknown>) => translate(messages, key, values),
    [messages],
  );
}

export { DEFAULT_UI_LANGUAGE };
